import React, { useEffect, useMemo, useState } from 'react';

import { deleteUser, login } from '../utils/Network';
import { getAge, getUser } from '../utils';
import { resolveMediaUrl } from '../../utils/Network';
import CityName from '../components/CityName';
import UserTalentList from '../components/UserTalentList';
import Modal from 'react-modal';
import UsersFilterWidget from '../components/UsersFilterWidget';
import { parseUsersListFilters } from '../utils/usersFilterQuery';
import { buildPaginationItems } from '../utils/cmsPagination';
import { useCmsUsersList } from '../hooks/useCmsUsersList';

function userListInitial(item) {
    const fn = item.first_name && String(item.first_name).trim();
    const ln = item.last_name && String(item.last_name).trim();
    const em = item.email && String(item.email).trim();
    const name = fn || ln || em || '?';
    return name.charAt(0).toUpperCase();
}

function hslBackgroundForId(id) {
    const n = Number(id) || 0;
    const h = (n * 47) % 360;
    return `hsl(${h}, 55%, 42%)`;
}

function range(start, end) {
    return Array.apply(0, Array(end)).map((element, index) => index + start);
}

export default function UsersContent({ page = 1, urlParams }) {
    const searchKey = urlParams.toString();
    const parsed = useMemo(() => parseUsersListFilters(searchKey), [searchKey]);

    const [show_filter, setShowFilter] = useState(false);
    const [show_delete, setShowDelete] = useState(false);

    const [filter_course_value, setFilterCourseValue] = useState(null);
    const [filter_is_verified_value, setFilterIsVerifiedValue] = useState(null);
    const [filter_talent_value, setFilterTalentValue] = useState(null);
    const [filter_gender_value, setFilterGenderValue] = useState(null);
    const [filter_size_value, setFilterSizeValue] = useState(null);

    const [filter_from_year_value, setFilterFromYearValue] = useState(null);
    const [filter_from_month_value, setFilterFromMonthValue] = useState(null);

    const [filter_to_year_value, setFilterToYearValue] = useState(null);
    const [filter_to_month_value, setFilterToMonthValue] = useState(null);

    const [filter_birth_country_value, setFilterBirthCountryValue] = useState(null);
    const [filter_birth_city_value, setFilterBirthCityValue] = useState(null);
    const [filter_living_country_value, setFilterLivingCountryValue] = useState(null);
    const [filter_living_city_value, setFilterLivingCityValue] = useState(null);

    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
    const [selected_user_id, setSelectedUserId] = useState(false);
    const [modal_password, setModalPassword] = useState(false);
    const [modal_error, setModalError] = useState(false);
    const [modal_loading, setModalLoading] = useState(false);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [failedImageIds, setFailedImageIds] = useState(() => new Set());

    const markImageFailed = (id) => {
        setFailedImageIds((current) => {
            if (current.has(id)) return current;
            const next = new Set(current);
            next.add(id);
            return next;
        });
    };

    const admin = getUser();

    const currentPage = Number(page) || 1;
    const filterSize = Number(parsed.filter_size) || 10;

    const {
        data: usersData,
        isLoading: usersLoading,
        isFetching: usersFetching,
        isError: usersError,
    } = useCmsUsersList(currentPage, parsed.filterQuery);

    const users = usersData?.results ?? [];
    const count = usersData?.count ?? null;

    const pages = useMemo(() => {
        if (count == null || count === 0) {
            return [];
        }
        const page_count =
            count % filterSize === 0
                ? parseInt(count / filterSize, 10)
                : parseInt(count / filterSize, 10) + 1;
        return page_count > 0 ? range(1, page_count) : [];
    }, [count, filterSize]);

    const paginationItems = useMemo(() => {
        if (!pages || !pages.length) return [];
        return buildPaginationItems(currentPage, pages.length);
    }, [pages, currentPage]);

    const totalPages = pages ? pages.length : 0;
    const canGoPrev = totalPages > 0 && currentPage > 1;
    const canGoNext = totalPages > 0 && currentPage < totalPages;

    const listBusy = usersLoading && !usersData;
    const filterPanelVisible = show_filter || parsed.hasActiveFilters;

    useEffect(() => {
        setFilterCourseValue(parsed.filter_course);
        setFilterIsVerifiedValue(parsed.filter_is_verified);
        setFilterTalentValue(parsed.filter_talent);
        setFilterGenderValue(parsed.filter_gender);
        setFilterBirthCountryValue(parsed.filter_birth_country);
        setFilterBirthCityValue(parsed.filter_birth_city);
        setFilterLivingCountryValue(parsed.filter_living_country);
        setFilterLivingCityValue(parsed.filter_living_city);
        setFilterFromYearValue(parsed.filter_from_year);
        setFilterFromMonthValue(parsed.filter_from_month);
        setFilterToYearValue(parsed.filter_to_year);
        setFilterToMonthValue(parsed.filter_to_month);
        setFilterSizeValue(parsed.filter_size);
    }, [parsed]);

    const openConfirmModal = (user_id) => {
        setSelectedUserId(user_id);
        setConfirmModalIsOpen(true);
    };

    const closeConfirmModal = () => {
        setConfirmModalIsOpen(false);
    };

    const handleChange = (e) => {
        switch (e.target.name) {
            case 'modal_password':
                setModalPassword(e.target.value);
                break;
            default:
                break;
        }
    };

    const modalSubmit = () => {
        deleteThisUser();
    };

    const deleteThisUser = () => {
        setModalLoading(true);
        setModalError(false);
        if (!modal_password) {
            setModalError('Please Enter Password');
            setModalLoading(false);
            return;
        }

        login({ email: admin.email, password: modal_password }).then((data) => {
            setModalLoading(false);
            if (data.tokens) {
                setDeletingUserId(selected_user_id);
                deleteUser(selected_user_id);
                window.location.href = '/cms/users';
            } else {
                setModalError(data.detail);
            }
        });
    };

    const showFilters = (e) => {
        e.preventDefault();
        setShowFilter(!show_filter);
    };

    const showDelete = (e) => {
        e.preventDefault();
        setShowDelete(!show_delete);
    };

    const onValue = (value, name) => {
        switch (name) {
            case 'course':
                setFilterCourseValue(value);
                break;
            case 'is_verified':
                setFilterIsVerifiedValue(value);
                break;
            case 'talent':
                setFilterTalentValue(value);
                break;
            case 'from_year':
                setFilterFromYearValue(value);
                break;
            case 'from_month':
                setFilterFromMonthValue(value);
                break;
            case 'to_year':
                setFilterToYearValue(value);
                break;
            case 'to_month':
                setFilterToMonthValue(value);
                break;
            case 'gender':
                setFilterGenderValue(value);
                break;
            case 'size':
                setFilterSizeValue(value);
                break;
            case 'country':
                setFilterBirthCountryValue(value);
                break;
            case 'city':
                setFilterBirthCityValue(value);
                break;
            case 'living_country':
                setFilterLivingCountryValue(value);
                break;
            case 'living_city':
                setFilterLivingCityValue(value);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Modal
                closeTimeoutMS={500}
                isOpen={confirmModalIsOpen}
                onRequestClose={closeConfirmModal}
                className="Modal confirm-modal"
                overlayClassName="Overlay"
            >
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" onClick={closeConfirmModal}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <h4 class="modal-title">Delete User</h4>
                    </div>
                    <div class="modal-body">
                        <div className="" style={{ minHeight: 100 }}>
                            <div className="col-md-12 mb-10 text-light">
                                To confirm Delete, Please enter your password
                            </div>
                            <div className="col-md-8">
                                <div class="input-group">
                                    <span class="input-group-addon">
                                        <i class="fa fa-lock"></i>
                                    </span>
                                    <input
                                        type="password"
                                        className="form-control modal-password"
                                        onChange={handleChange}
                                        placeholder="Password"
                                        name="modal_password"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <button className="btn btn-primary btn-rounded" onClick={modalSubmit}>
                                    DELETE
                                </button>
                            </div>
                        </div>
                        {modal_error && <div className="alert alert-danger">{modal_error}</div>}
                        {modal_loading && (
                            <div className="alert alert-primary text-color-light">Please wait...</div>
                        )}
                    </div>
                </div>
            </Modal>
            <div className="content-wrapper">
                

                <section className="content">
                    <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                        <div className="row users-filter-page-row">
                            <div className="col-md-12 users-filter-page-col">
                                <UsersFilterWidget
                                    visible={filterPanelVisible}
                                    filter_first_name={parsed.filter_first_name}
                                    filter_last_name={parsed.filter_last_name}
                                    filter_email={parsed.filter_email}
                                    filter_gender_value={filter_gender_value}
                                    filter_talent_value={filter_talent_value}
                                    filter_course_value={filter_course_value}
                                    filter_from_year_value={filter_from_year_value}
                                    filter_from_month_value={filter_from_month_value}
                                    filter_to_year_value={filter_to_year_value}
                                    filter_to_month_value={filter_to_month_value}
                                    filter_birth_country_value={filter_birth_country_value}
                                    filter_birth_city_value={filter_birth_city_value}
                                    filter_living_country_value={filter_living_country_value}
                                    filter_living_city_value={filter_living_city_value}
                                    filter_size_value={filter_size_value}
                                    filter_is_verified_value={filter_is_verified_value}
                                    onValue={onValue}
                                />
                                <div className="box box-primary mt-0 border-top-0 ">
                                    <div className="box-header with-border">
                                        <h3 className="box-title">
                                            All talents
                                            {count != null && (
                                                <span style={{ fontSize: 11, marginLeft: '0.35em' }}>
                                                    ({count})
                                                </span>
                                            )}
                                        </h3>
                                        <a
                                            href="/cms/user"
                                            title="Add New"
                                            className="btn btn-cm margin-r-5 pull-right"
                                        >
                                            <i className="fa fa-plus"></i>
                                        </a>
                                        <button
                                            type="button"
                                            title="Filters"
                                            className="btn btn-cm margin-r-5 pull-right"
                                            onClick={showFilters}
                                        >
                                            <i className="fa fa-filter"></i>
                                        </button>
                                        <button
                                            type="button"
                                            title="Show Delete Button"
                                            className="btn btn-cm margin-r-5 pull-right"
                                            onClick={showDelete}
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                    <div className="box-body no-padding">
                                        {usersError && (
                                            <div className="padding-10 text-danger">
                                                Could not load users. Please try again.
                                            </div>
                                        )}
                                        {listBusy && !usersError && (
                                            <div className="padding-10 text-muted">Loading…</div>
                                        )}
                                        <table
                                            id="example1"
                                            className={`table table-striped${usersFetching ? ' opacity-70' : ''}`}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Talent</th>
                                                    <th className="hide-on-small">Age</th>
                                                    <th className="hide-on-small">Gender</th>
                                                    <th className="hide-on-small">POB</th>
                                                    <th className="hide-on-small">POR</th>
                                                    <th style={{ width: 50 }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users &&
                                                    users.map((item) => (
                                                        <tr
                                                            key={item.id}
                                                            className={
                                                                deletingUserId === item.id ||
                                                                deletingUserId === item.user
                                                                    ? 'loading'
                                                                    : ''
                                                            }
                                                        >
                                                            <td>
                                                                <div className="img-container ">
                                                                    <a href={`/cms/usertalent/${item.id}`}>
                                                                        {item.image && !failedImageIds.has(item.id) ? (
                                                                            <img
                                                                                src={resolveMediaUrl(item.image)}
                                                                                className="pull-left img-list img-circle margin-r-10"
                                                                                alt={`${item.first_name || ''} ${item.last_name || ''}`.trim()}
                                                                                onError={() => markImageFailed(item.id)}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                className="pull-left img-list img-circle margin-r-10 users-letter-avatar"
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        hslBackgroundForId(
                                                                                            item.user || item.id
                                                                                        ),
                                                                                }}
                                                                                aria-hidden
                                                                            >
                                                                                {userListInitial(item)}
                                                                            </div>
                                                                        )}
                                                                    </a>
                                                                </div>

                                                                <a
                                                                    href={`/cms/usertalent/${item.id}`}
                                                                    className="link-color"
                                                                >
                                                                    {item.first_name && (
                                                                        <p className="text-capitalize mb-0 text-light kfont">
                                                                            {item.first_name} {item.last_name}
                                                                        </p>
                                                                    )}
                                                                    {!item.first_name && (
                                                                        <p className="text-light">{item.email}</p>
                                                                    )}
                                                                    <p
                                                                        title="Registered date"
                                                                        style={{ color: '#949090', fontSize: 11 }}
                                                                    >
                                                                        {item.created_at &&
                                                                            item.created_at.split(' ')[0]}
                                                                    </p>
                                                                </a>
                                                            </td>

                                                            <td>
                                                                <UserTalentList user_id={item.user} />
                                                            </td>
                                                            <td className="hide-on-small">
                                                                <td>
                                                                    {item.birth_date} <br />
                                                                    <small>{getAge(item.birth_date)} Years old</small>
                                                                </td>
                                                            </td>
                                                            <td className="hide-on-small">
                                                                {item.gender === 0
                                                                    ? 'Male'
                                                                    : item.gender === 1
                                                                      ? 'Female'
                                                                      : 'Other'}
                                                            </td>
                                                            <td className="hide-on-small">
                                                                <CityName id={item.city_id} />
                                                            </td>
                                                            <td className="hide-on-small">
                                                                <CityName id={item.livingcity_id} />
                                                            </td>
                                                            <td>
                                                                <div className="btn-group">
                                                                    {show_delete && (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    openConfirmModal(item.user);
                                                                                }}
                                                                                className="btn btn-default btn-sm"
                                                                            >
                                                                                <i className="fa fa-trash"></i>
                                                                            </button>
                                                                            <a
                                                                                className="btn btn-default btn-sm mt-1"
                                                                                href={`/cms/user/${item.id}`}
                                                                            >
                                                                                <i className="fa fa-edit"></i>
                                                                            </a>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th>Name</th>
                                                    <th className="hide-on-small">Rating</th>
                                                    <th>Talent</th>
                                                    <th className="hide-on-small">Age</th>
                                                    <th className="hide-on-small">Gender</th>
                                                    <th className="hide-on-small">POB</th>
                                                    <th className="hide-on-small">POR</th>
                                                    <th></th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                <ul className="pagination cms-pagination m-0 float-right">
                                    <li className={`page-item${!canGoPrev ? ' disabled' : ''}`}>
                                        <a
                                            className="page-link"
                                            href={
                                                canGoPrev
                                                    ? `/cms/users/${currentPage - 1}?${urlParams}`
                                                    : '#'
                                            }
                                            onClick={!canGoPrev ? (e) => e.preventDefault() : undefined}
                                            aria-disabled={!canGoPrev}
                                        >
                                            «
                                        </a>
                                    </li>
                                    {paginationItems.map((entry, idx) => (
                                        <li
                                            key={entry === 'ellipsis' ? `e-${idx}` : entry}
                                            className="page-item"
                                        >
                                            {entry === 'ellipsis' ? (
                                                <span
                                                    className="page-link disabled"
                                                    style={{ border: 'none', background: 'transparent' }}
                                                >
                                                    …
                                                </span>
                                            ) : (
                                                <a
                                                    className={
                                                        entry === currentPage ? 'page-link active' : 'page-link'
                                                    }
                                                    href={`/cms/users/${entry}?${urlParams}`}
                                                >
                                                    {entry}
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                    <li className={`page-item${!canGoNext ? ' disabled' : ''}`}>
                                        <a
                                            className="page-link"
                                            href={
                                                canGoNext
                                                    ? `/cms/users/${currentPage + 1}?${urlParams}`
                                                    : '#'
                                            }
                                            onClick={!canGoNext ? (e) => e.preventDefault() : undefined}
                                            aria-disabled={!canGoNext}
                                        >
                                            »
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
